/**
 * GoalsProgressWidget — "How much more to hit your goals" card.
 *
 * Shown on the Live Performance Dashboard. Reads `data.goals` from the
 * live-dashboard API response (already scaled to the active view — Week or
 * Month). Each row shows: label, current / target, progress bar and a
 * "X more" or "Target hit" chip. When no target is set, prompts the broker
 * to open Set Goals.
 */
import React from 'react';
import {
  Award, Check, ClipboardCheck, DollarSign, FileCheck, FilePlus, Send,
  Sparkles, Target, TrendingUp, Trophy,
} from 'lucide-react';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';
import { formatCompactCurrency } from './gameplanUtils';

const ROW_META = {
  'New files':          { icon: FilePlus,        tone: 'from-sky-400 to-blue-600',       chip: 'bg-sky-50 text-sky-700 dark:bg-sky-500/15 dark:text-sky-300' },
  'Client invites':     { icon: Send,            tone: 'from-violet-400 to-purple-600',  chip: 'bg-violet-50 text-violet-700 dark:bg-violet-500/15 dark:text-violet-300' },
  'Portal completions': { icon: ClipboardCheck,  tone: 'from-amber-400 to-orange-500',   chip: 'bg-amber-50 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300' },
  'Proposals sent':     { icon: FileCheck,       tone: 'from-indigo-400 to-indigo-600',  chip: 'bg-indigo-50 text-indigo-700 dark:bg-indigo-500/15 dark:text-indigo-300' },
  'Lodgements':         { icon: TrendingUp,      tone: 'from-blue-400 to-blue-700',      chip: 'bg-blue-50 text-blue-700 dark:bg-blue-500/15 dark:text-blue-300' },
  'Approvals':          { icon: Award,           tone: 'from-emerald-400 to-emerald-600',chip: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300' },
  'Settlements':        { icon: Trophy,          tone: 'from-green-500 to-emerald-700',  chip: 'bg-green-50 text-green-700 dark:bg-green-500/15 dark:text-green-300' },
  'Est. revenue':       { icon: DollarSign,      tone: 'from-emerald-500 to-teal-700',   chip: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300' },
};

const fmt = (n, isCurrency) => {
  if (isCurrency) return formatCompactCurrency(n || 0);
  return Number(n || 0).toLocaleString('en-AU', { maximumFractionDigits: 0 });
};

const GoalsProgressWidget = ({ goals, view = 'week', onOpenSetGoals }) => {
  const rows = goals?.rows || [];
  const hasTargets = Boolean(goals?.has_targets);
  const scopeLabel = view === 'month' ? 'this month' : view === 'week' ? 'this week' : 'this period';

  // Empty state — no monthly settlement target set yet.
  if (!hasTargets) {
    return (
      <div className="rounded-2xl border border-dashed border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-sm p-6 text-center"
           data-testid="goals-progress-empty">
        <div className="mx-auto w-12 h-12 rounded-full bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 flex items-center justify-center mb-3">
          <Target className="w-5 h-5" />
        </div>
        <h3 className="text-sm font-bold tracking-tight text-slate-900 dark:text-white">Set a goal to see progress here</h3>
        <p className="text-xs text-slate-500 dark:text-slate-400 mt-1.5 max-w-md mx-auto">
          Enter your monthly settlement target in <span className="font-semibold">Set Goals</span> —
          we&apos;ll auto-derive the full activity plan (files, invites, completions, proposals, lodgements, approvals) and show how far you are each week.
        </p>
        {onOpenSetGoals && (
          <Button size="sm" onClick={onOpenSetGoals} className="mt-4 bg-slate-900 hover:bg-slate-800 text-white" data-testid="goals-progress-set-btn">
            <Target className="w-4 h-4 mr-1.5" /> Open Set Goals
          </Button>
        )}
      </div>
    );
  }

  // Sum the "remaining" so we can show a summary in the header.
  const behindCount = rows.filter((r) => r.remaining > 0).length;
  const doneCount = rows.filter((r) => r.on_track).length;
  const avgProgress = rows.length
    ? Math.round(rows.reduce((s, r) => s + Math.min(100, r.achievement || 0), 0) / rows.length)
    : 0;

  return (
    <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm overflow-hidden"
         data-testid="goals-progress-widget">
      {/* Header — with donut summarising overall progress */}
      <div className="flex items-center gap-3 p-4 border-b border-slate-100 dark:border-slate-800 bg-gradient-to-r from-slate-50 via-white to-white dark:from-slate-900/70 dark:via-slate-900 dark:to-slate-900">
        {/* Overall progress donut */}
        <div className="relative w-14 h-14 shrink-0" aria-label={`${avgProgress}% average progress`}>
          <svg viewBox="0 0 36 36" className="w-14 h-14 -rotate-90">
            <circle cx="18" cy="18" r="15.9" fill="none" strokeWidth="3"
                    className="stroke-slate-200 dark:stroke-slate-700" />
            <circle cx="18" cy="18" r="15.9" fill="none" strokeWidth="3" strokeLinecap="round"
                    strokeDasharray={`${Math.min(100, avgProgress)} 100`}
                    className={cn(
                      'transition-all duration-700',
                      avgProgress >= 100 ? 'stroke-emerald-500'
                        : avgProgress >= 70 ? 'stroke-amber-500'
                          : 'stroke-slate-800 dark:stroke-slate-200'
                    )} />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-[13px] font-black tabular-nums text-slate-900 dark:text-white leading-none">{avgProgress}%</span>
          </div>
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-black tracking-tight text-slate-900 dark:text-white leading-tight">
            Goal progress
          </h3>
          <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
            How much more to hit your goal {scopeLabel}
          </p>
        </div>
        <div className="flex flex-col items-end gap-1">
          {doneCount > 0 && (
            <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full
                             bg-emerald-100 text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-300">
              <Check className="w-2.5 h-2.5" /> {doneCount} hit
            </span>
          )}
          {behindCount > 0 && (
            <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full
                             bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300">
              {behindCount} to go
            </span>
          )}
        </div>
      </div>

      {/* Tile grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-slate-100 dark:bg-slate-800">
        {rows.map((r) => {
          const meta = ROW_META[r.label] || { icon: Target, tone: 'from-slate-400 to-slate-600' };
          const Icon = meta.icon;
          const pct = Math.min(100, Math.max(0, r.achievement || 0));
          const hit = r.status === 'hit';
          // Traffic-light glow driven by pace-vs-elapsed grading from the backend.
          // Fallback (older payload without `status`): use raw achievement %.
          const status = r.status || (hit ? 'hit' : r.achievement >= 70 ? 'on_track' : r.achievement >= 40 ? 'verge' : 'behind');
          const glow = {
            hit:      'ring-1 ring-emerald-200/70 dark:ring-emerald-700/50 shadow-[inset_0_0_0_1px_rgba(16,185,129,0.15),0_0_16px_-6px_rgba(16,185,129,0.55)] dark:shadow-[inset_0_0_0_1px_rgba(16,185,129,0.35),0_0_20px_-6px_rgba(16,185,129,0.55)]',
            on_track: 'ring-1 ring-emerald-200/70 dark:ring-emerald-700/50 shadow-[inset_0_0_0_1px_rgba(16,185,129,0.10),0_0_14px_-8px_rgba(16,185,129,0.45)] dark:shadow-[inset_0_0_0_1px_rgba(16,185,129,0.25),0_0_18px_-8px_rgba(16,185,129,0.45)]',
            verge:    'ring-1 ring-amber-200/70 dark:ring-amber-700/50 shadow-[inset_0_0_0_1px_rgba(245,158,11,0.10),0_0_14px_-8px_rgba(245,158,11,0.55)] dark:shadow-[inset_0_0_0_1px_rgba(245,158,11,0.25),0_0_20px_-8px_rgba(245,158,11,0.55)]',
            behind:   'ring-1 ring-rose-200/70 dark:ring-rose-700/50 shadow-[inset_0_0_0_1px_rgba(244,63,94,0.12),0_0_14px_-8px_rgba(244,63,94,0.6)] dark:shadow-[inset_0_0_0_1px_rgba(244,63,94,0.30),0_0_22px_-8px_rgba(244,63,94,0.6)]',
          }[status] || '';
          const statusTone = {
            hit:      'text-emerald-700 dark:text-emerald-300',
            on_track: 'text-emerald-700 dark:text-emerald-300',
            verge:    'text-amber-700 dark:text-amber-300',
            behind:   'text-rose-700 dark:text-rose-300',
          }[status] || 'text-slate-500 dark:text-slate-400';
          const statusLabel = {
            hit:      'Target hit — nice one',
            on_track: 'On track',
            verge:    'At risk',
            behind:   'Behind',
          }[status] || '';
          return (
            <div
              key={r.label}
              className={cn(
                'relative p-3.5 bg-white dark:bg-slate-900 group hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors',
                glow,
              )}
              data-testid={`goal-tile-${r.label.toLowerCase().replace(/\s+/g, '-')}`}
              data-status={status}
            >
              {/* Accent top bar — matches the metric's colour palette */}
              <div className={cn('absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r', meta.tone, 'opacity-70')} />
              <div className="flex items-start justify-between gap-2">
                <div className={cn(
                  'w-8 h-8 rounded-lg bg-gradient-to-br text-white flex items-center justify-center shadow-sm',
                  meta.tone,
                )}>
                  <Icon className="w-4 h-4" />
                </div>
                {hit ? (
                  <span className="inline-flex items-center gap-1 text-[9.5px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded-full
                                   bg-emerald-100 text-emerald-800 dark:bg-emerald-500/25 dark:text-emerald-300">
                    <Check className="w-2.5 h-2.5" /> Hit
                  </span>
                ) : (
                  <span className={cn('text-[10px] font-bold tabular-nums', statusTone)}>{Math.round(pct)}%</span>
                )}
              </div>
              {/* Label */}
              <div className="mt-2.5 text-[10.5px] uppercase tracking-[0.12em] font-bold text-slate-500 dark:text-slate-400">
                {r.label}
              </div>
              {/* Hero number */}
              <div className="mt-0.5 flex items-baseline gap-1">
                <span className="text-2xl font-black tabular-nums text-slate-900 dark:text-white leading-none">
                  {fmt(r.current, r.is_currency)}
                </span>
                <span className="text-[11px] text-slate-400 dark:text-slate-500 tabular-nums">
                  / {fmt(r.target, r.is_currency)}
                </span>
              </div>
              {/* Progress bar */}
              <div className="mt-2 h-1.5 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
                <div
                  className={cn(
                    'h-full rounded-full transition-all duration-700 bg-gradient-to-r',
                    hit ? 'from-emerald-400 to-emerald-600' : meta.tone,
                  )}
                  style={{ width: `${pct}%` }}
                />
              </div>
              {/* Status text — traffic-light coloured */}
              <div className={cn('mt-1.5 text-[11px] font-semibold flex items-center gap-1', statusTone)}>
                <span className={cn(
                  'w-1.5 h-1.5 rounded-full',
                  status === 'hit' || status === 'on_track' ? 'bg-emerald-500'
                    : status === 'verge' ? 'bg-amber-500'
                      : 'bg-rose-500',
                )} />
                {hit ? statusLabel : (
                  <>
                    <span className="tabular-nums">{fmt(r.remaining, r.is_currency)}</span> to go
                    <span className="text-slate-300 dark:text-slate-600 mx-0.5">·</span>
                    <span>{statusLabel}</span>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer */}
      <div className="px-4 py-2 bg-slate-50 dark:bg-slate-900/60 border-t border-slate-100 dark:border-slate-800 text-[10.5px] text-slate-500 dark:text-slate-400 flex items-center gap-1.5">
        <Sparkles className="w-3 h-3 text-slate-400" />
        Auto-derived from your Set-Goals monthly settlement target
        {view === 'week' && ' · scaled to 5 weekdays'}
        {onOpenSetGoals && (
          <button type="button" onClick={onOpenSetGoals}
            className="ml-auto text-slate-700 hover:text-slate-900 dark:text-slate-300 dark:hover:text-white font-semibold underline underline-offset-2"
            data-testid="goals-progress-adjust-btn">
            Adjust goals
          </button>
        )}
      </div>
    </div>
  );
};

export default GoalsProgressWidget;
export { GoalsProgressWidget };
